import {DateRange} from '@/domain/useCases/createImageSearchRequest';

import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';

import {SxProps} from '@mui/material';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import dayjs, {Dayjs} from 'dayjs';
import React, {FC} from 'react';
import {useTranslation} from 'react-i18next';

import PrimaryButton from '../Button';
import CalendarPicker from '../CalendarPicker';
import 'dayjs/locale/es';


export type Props = {
  dateRange: Partial<DateRange>,
  isRequestEnabled: boolean,
  hasDrawnGeometry: boolean
  onDateRangeChange: (dateRange: Partial<DateRange>) => void,
  onDrawStart: () => void,
  onRequest: () => void
};

const RequestImagesPanel: FC<Props> = ({
  dateRange,
  isRequestEnabled,
  hasDrawnGeometry,
  onDateRangeChange,
  onDrawStart,
  onRequest
}) => {
  const {t} = useTranslation();

  const handleStartDayChange = (newDate?: Dayjs) => onDateRangeChange({...dateRange, start: newDate});
  const handleEndDayChange = (newDate?: Dayjs) => onDateRangeChange({...dateRange, end: newDate});

  return  <Stack direction="column" sx={styles.container}>
    <Stack direction="column" sx={styles.header}>
      <Typography variant="h5" sx={styles.headerTitle}>{t('requestImages.title')}</Typography>
      <Typography sx={styles.headerDescription}>{t('requestImages.description')}</Typography>
    </Stack>
    <Stack direction="column" sx={styles.formContainer}>
      <Stack direction="column">
        <Stack direction="row" sx={styles.fieldTitle}>
          <LocationOnOutlinedIcon sx={styles.fieldIcon}/>
          <Typography sx={styles.fieldLabel}>{t('requestImages.areaOfInterest')} *</Typography>
        </Stack>
        <PrimaryButton
          label={hasDrawnGeometry ? t('requestImages.redrawOnMap') :  t('requestImages.drawOnMap')}
          onClick={onDrawStart}
          sx={styles.button}
        />
      </Stack>
      <Stack direction="row" sx={styles.dateContainer}>
        <CalendarPicker
          label={t('requestImages.start')}
          value={dateRange.start}
          minDate={undefined}
          maxDate={dayjs()}
          onChange={handleStartDayChange}
          required={true}
          readOnly={false}
          disableToday={true}
        />
        <CalendarPicker
          label={t('requestImages.end')}
          value={dateRange.end}
          minDate={dateRange.start ? dateRange.start : dayjs()}
          maxDate={dayjs()}
          onChange={handleEndDayChange}
          required={true}
          readOnly={!dateRange.start}
        />
      </Stack>
      <Stack direction="row" sx={styles.buttonContainer}>
        <PrimaryButton
          label={t('requestImages.request')}
          onClick={() => onRequest()}
          sx={styles.button}
          disabled={!isRequestEnabled}
        />
      </Stack>
    </Stack>
  </Stack>;
};
export default RequestImagesPanel;

const styles: Record<string, SxProps> = {
  container: {
    gap: 2,
    p:1,
    bgcolor: 'grey.200'
  },
  header: {
    px: 2,
    alignItems: 'center'
  },
  headerTitle: {
    fontWeight: 'bold',
    color: 'primary.main'
  },
  headerDescription: {
    color: 'text.primary'
  },
  formContainer: {
    bgcolor: 'common.white',
    px: 2,
    mb: 2,
    gap: 3,
    borderRadius: 2,
    py: 2
  },
  fieldTitle: {
    alignItems: 'center',
    gap: 1
  },
  fieldIcon: {
    fontSize: '20px',
    color: 'primary.main'
  },
  fieldLabel: {
    ml: 0.5,
    mb: 0.25,
    color: 'text.primary'
  },
  textArea: {
    mt: 1,
    bgcolor: 'grey.200'
  },
  dateContainer: {
    gap: 2,
    mb: 4
  },
  buttonContainer: {
    justifyContent: 'flex-end'
  },
  button: {
    minWidth: '200px'
  }
};
